/**
 * CAPTCHA Helper Functions
 * 
 * Google reCAPTCHA v2, v3 ve Cloudflare Turnstile desteği
 * Dinamik site key desteği ile
 */

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      render: (container: HTMLElement | string, options: {
        sitekey: string;
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: (error?: any) => void;
      }) => number;
      reset: (widgetId?: number) => void;
      getResponse: (widgetId?: number) => string;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
      enterprise?: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
    turnstile?: {
      render: (container: HTMLElement | string, options: {
        sitekey: string;
        callback?: (token: string) => void;
        'error-callback'?: (error?: any) => void;
        'expired-callback'?: () => void;
      }) => string;
      reset: (widgetId?: string) => void;
      getResponse: (widgetId?: string) => string;
      remove: (widgetId?: string) => void;
    };
  }
}

// ==================== reCAPTCHA v2 ====================

/**
 * reCAPTCHA v2 script'inin yüklenip yüklenmediğini kontrol et
 */
export function isRecaptchaLoaded(): boolean {
  return typeof window !== 'undefined' && typeof window.grecaptcha !== 'undefined';
}

/**
 * reCAPTCHA v2 script'ini yükle
 * @param scriptUrl - Script URL (opsiyonel, varsayılan: Google reCAPTCHA v2)
 */
export function loadRecaptchaScript(scriptUrl?: string): Promise<void> {
  if (isRecaptchaLoaded()) {
    
    return Promise.resolve();
  }

  let url = scriptUrl || 'https://www.google.com/recaptcha/api.js?render=explicit';

  // Cache bypass için timestamp ekle (development için)
  if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
    const separator = url.includes('?') ? '&' : '?';
    url = `${url}${separator}_=${Date.now()}`;
  }

  // Mevcut script'i kontrol et ve kaldır (cache sorununu önlemek için)
  const existingScript = document.querySelector('script[src*="recaptcha"]') as HTMLScriptElement;
  if (existingScript) {
    
    existingScript.remove();
    // Window'dan da temizle
    if (window.grecaptcha) {
      delete (window as any).grecaptcha;
    }
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      
      waitForRecaptcha().then(resolve).catch(reject);
    };
    script.onerror = (error) => {
      console.error('[captcha] reCAPTCHA script load error:', error);
      reject(new Error('reCAPTCHA script yüklenemedi'));
    };
    document.head.appendChild(script);
  });
}

/**
 * reCAPTCHA v2 widget'ı render et
 * @param containerId - Widget'ın render edileceği container ID
 * @param siteKey - reCAPTCHA site key (zorunlu)
 * @param callback - Token alındığında çağrılacak callback
 * @returns Widget ID
 */
export function renderRecaptchaWidget(
  containerId: string,
  siteKey: string,
  callback: (token: string) => void
): Promise<number> {
  return new Promise((resolve, reject) => {
    if (!siteKey) {
      reject(new Error('reCAPTCHA site key gerekli'));
      return;
    }

    if (!isRecaptchaLoaded()) {
      reject(new Error('reCAPTCHA script yüklenmedi'));
      return;
    }

    window.grecaptcha!.ready(() => {
      try {
        // Container'ı kontrol et - eğer zaten bir widget render edilmişse, önce onu kaldır
        const container = document.getElementById(containerId);
        if (!container) {
          reject(new Error(`Container with id "${containerId}" not found`));
          return;
        }
        
        // Container'ın içindeki tüm child element'leri temizle
        // (reCAPTCHA widget'ı container içine render edilir)
        // ÖNEMLİ: innerHTML temizleme, reCAPTCHA'nın internal state'ini temizlemez
        // Bu yüzden grecaptcha'nın kendi reset/remove metodlarını kullanmalıyız
        // Ama şimdilik innerHTML temizleme yeterli olabilir
        container.innerHTML = '';
        
        // Eğer container'da zaten bir widget varsa (grecaptcha internal state'inde),
        // render hata verebilir. Bu durumda try-catch ile yakalayıp hata mesajını kontrol edelim
        const widgetId = window.grecaptcha!.render(containerId, {
          sitekey: siteKey,
          callback: (token: string) => {
            
            callback(token);
          },
          'expired-callback': () => {
            
            // Token süresi doldu, reset et
            window.grecaptcha!.reset(widgetId);
          },
          'error-callback': (error: any) => {
            
            reject(new Error('reCAPTCHA hatası oluştu: ' + (error?.message || 'Bilinmeyen hata')));
          },
        });
        
        resolve(widgetId);
      } catch (error: any) {
        // Eğer "already been rendered" hatası varsa, container'ı temizle ve tekrar dene
        if (error?.message?.includes('already been rendered')) {
          const container = document.getElementById(containerId);
          if (container) {
            // Container'ı tamamen temizle
            container.innerHTML = '';
            
            // Grecaptcha'nın internal state'ini temizlemek için container'ı DOM'dan kaldırıp geri ekle
            const parent = container.parentNode;
            if (parent) {
              const nextSibling = container.nextSibling;
              parent.removeChild(container);
              const newContainer = document.createElement('div');
              newContainer.id = containerId;
              if (nextSibling) {
                parent.insertBefore(newContainer, nextSibling);
              } else {
                parent.appendChild(newContainer);
              }
            }
            
            // Kısa bir bekleme sonrası tekrar dene (DOM güncellemesi için)
            setTimeout(() => {
              try {
                const retryWidgetId = window.grecaptcha!.render(containerId, {
                  sitekey: siteKey,
                  callback: (token: string) => {
                    callback(token);
                  },
                  'expired-callback': () => {
                    window.grecaptcha!.reset(retryWidgetId);
                  },
                  'error-callback': (error: any) => {
                    reject(new Error('reCAPTCHA hatası oluştu: ' + (error?.message || 'Bilinmeyen hata')));
                  },
                });
                resolve(retryWidgetId);
              } catch (retryError: any) {
                // Retry'da da hata alırsak, bu sefer gerçekten hata var demektir
                console.error('[captcha] reCAPTCHA v2 render retry error:', retryError);
                // Silent fail - widget render edilemese bile form çalışmaya devam etmeli
                // Bu durumda widget ID olmadan resolve et (captcha token olmadan devam eder)
                resolve(-1); // Geçersiz widget ID - captcha token alınamaz ama form çalışır
              }
            }, 200); // Biraz daha uzun bekleme
            return; // İlk render'dan çık, retry'da resolve/reject yapılacak
          }
        }
        console.error('[captcha] reCAPTCHA v2 render error:', error);
        // Silent fail - widget render edilemese bile form çalışmaya devam etmeli
        resolve(-1); // Geçersiz widget ID
      }
    });
  });
}

/**
 * reCAPTCHA widget'ını reset et
 * @param widgetId - Widget ID (opsiyonel, belirtilmezse tüm widget'lar reset edilir)
 */
export function resetRecaptchaWidget(widgetId?: number): void {
  if (isRecaptchaLoaded() && window.grecaptcha) {
    window.grecaptcha.reset(widgetId);
  }
}

/**
 * reCAPTCHA token'ı al (v2 için)
 * @param widgetId - Widget ID
 * @returns Token string veya boş string
 */
export function getRecaptchaToken(widgetId?: number): string {
  if (!isRecaptchaLoaded() || !window.grecaptcha) {
    return '';
  }
  return window.grecaptcha.getResponse(widgetId) || '';
}

/**
 * reCAPTCHA'nın hazır olmasını bekle
 */
export function waitForRecaptcha(): Promise<void> {
  if (isRecaptchaLoaded()) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const maxAttempts = 50; // 5 saniye (50 * 100ms)
    let attempts = 0;

    const checkInterval = setInterval(() => {
      attempts++;
      if (isRecaptchaLoaded()) {
        clearInterval(checkInterval);
        resolve();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        reject(new Error('reCAPTCHA script yüklenemedi'));
      }
    }, 100);
  });
}

// ==================== reCAPTCHA v3 ====================

/**
 * reCAPTCHA v3 veya Enterprise script'inin yüklenip yüklenmediğini kontrol et
 */
export function isRecaptchaV3Loaded(): boolean {
  if (typeof window === 'undefined' || !window.grecaptcha) return false;
  return (
    typeof window.grecaptcha.execute === 'function' ||
    Boolean(window.grecaptcha.enterprise && typeof window.grecaptcha.enterprise.execute === 'function')
  );
}

const LOCALHOST_HINT = [
  '[reCAPTCHA Enterprise] Localhost için script yüklenemedi.',
  'Çözüm: Google Cloud Console → reCAPTCHA Enterprise → İlgili anahtarınızı açın.',
  '"Domain names" (Alan adları) listesine tam olarak şunları ekleyin: localhost ve 127.0.0.1',
  '(http://, port veya www eklemeyin). Değişiklik 5–30 dakika içinde yansır.',
  'Not: "Domain kontrolü" kapalı olsa bile bu listeye localhost eklemeniz gerekir.',
].join(' ');

let recaptchaV3LoadRetried = false;
/** Script hangi site key ile yüklendi (BWP'de key değişince yeniden yükle) */
let loadedRecaptchaV3SiteKey: string | null = null;

/** v3/Enterprise sağ alt rozetini gizler; formlarda RecaptchaLegalNotice gösterilmeli */
function setRecaptchaFloatingBadgeHidden(hidden: boolean): void {
  if (typeof document === 'undefined') return;
  document.body.classList.toggle('alsero-hide-recaptcha-badge', hidden);
}

function unloadRecaptchaV3Script(): void {
  const existingScript = document.querySelector('script[src*="recaptcha"]') as HTMLScriptElement;
  if (existingScript) {
    existingScript.remove();
  }
  if (typeof window !== 'undefined' && window.grecaptcha) {
    delete (window as unknown as { grecaptcha?: unknown }).grecaptcha;
  }
  loadedRecaptchaV3SiteKey = null;
  setRecaptchaFloatingBadgeHidden(false);
}

/**
 * reCAPTCHA v3 veya Enterprise script'ini yükle
 * @param siteKey - reCAPTCHA site key
 * @param scriptUrl - Opsiyonel: Enterprise için https://www.google.com/recaptcha/enterprise.js?render=KEY
 */
export function loadRecaptchaV3Script(siteKey: string, scriptUrl?: string | null): Promise<void> {
  if (isRecaptchaV3Loaded() && loadedRecaptchaV3SiteKey === siteKey) {
    return Promise.resolve();
  }
  if (isRecaptchaV3Loaded() && loadedRecaptchaV3SiteKey !== siteKey) {
    unloadRecaptchaV3Script();
  }

  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  const loadOne = (): Promise<void> => {
    let url =
      scriptUrl && scriptUrl.includes('recaptcha')
        ? scriptUrl
        : `https://www.google.com/recaptcha/api.js?render=${siteKey}`;

    if (process.env.NODE_ENV === 'development' || isLocalhost) {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}_=${Date.now()}`;
    }

    unloadRecaptchaV3Script();

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        waitForRecaptchaV3()
          .then(() => {
            loadedRecaptchaV3SiteKey = siteKey;
            setRecaptchaFloatingBadgeHidden(true);
            resolve();
          })
          .catch(reject);
      };
      script.onerror = () => {
        const hint = isLocalhost ? LOCALHOST_HINT : ' Ağ engeli veya anahtar domain ayarını kontrol edin.';
        console.error('[captcha] reCAPTCHA v3/Enterprise script load error:', hint);
        reject(new Error('reCAPTCHA script yüklenemedi. ' + hint));
      };
      document.head.appendChild(script);
    });
  };

  return loadOne().catch((err) => {
    if (isLocalhost && !recaptchaV3LoadRetried) {
      recaptchaV3LoadRetried = true;
      console.warn('[captcha] Localhost: 3 saniye sonra tekrar denenecek (domain listesi güncellemesi gecikmesi olabilir).');
      return new Promise<void>((resolve, reject) => {
        setTimeout(() => {
          loadOne().then(resolve).catch(reject);
        }, 3000);
      });
    }
    throw err;
  });
}

/**
 * reCAPTCHA v3 token'ı al (action ile)
 * @param siteKey - reCAPTCHA v3 site key
 * @param action - Action adı (örn: 'submit', 'contact_form')
 * @returns Token string
 */
export async function getRecaptchaV3Token(siteKey: string, action: string = 'submit'): Promise<string> {
  if (!isRecaptchaV3Loaded() || !window.grecaptcha) {
    throw new Error('reCAPTCHA v3 script yüklenmedi');
  }

  try {
    if (window.grecaptcha.enterprise && typeof window.grecaptcha.enterprise.execute === 'function') {
      return await window.grecaptcha.enterprise.execute(siteKey, { action });
    }
    return await window.grecaptcha.execute(siteKey, { action });
  } catch (error) {
    console.error('reCAPTCHA v3 token alınamadı:', error);
    throw error;
  }
}

/**
 * reCAPTCHA v3 veya Enterprise'ın hazır olmasını bekle
 */
export function waitForRecaptchaV3(): Promise<void> {
  if (isRecaptchaV3Loaded()) {
    if (window.grecaptcha?.enterprise?.ready) {
      return new Promise((resolve, reject) => {
        window.grecaptcha!.enterprise!.ready(() => resolve());
      });
    }
    if (window.grecaptcha?.ready) {
      return new Promise((resolve, reject) => {
        window.grecaptcha!.ready(() => resolve());
      });
    }
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const maxAttempts = 50; // 5 saniye (50 * 100ms)
    let attempts = 0;
    const checkInterval = setInterval(() => {
      attempts++;
      if (isRecaptchaV3Loaded()) {
        clearInterval(checkInterval);
        if (window.grecaptcha?.enterprise?.ready) {
          window.grecaptcha.enterprise!.ready(() => resolve());
        } else if (window.grecaptcha?.ready) {
          window.grecaptcha.ready(() => resolve());
        } else {
          resolve();
        }
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        reject(new Error('reCAPTCHA v3 script yüklenemedi'));
      }
    }, 100);
  });
}

// ==================== Cloudflare Turnstile ====================

/**
 * Cloudflare Turnstile script'inin yüklenip yüklenmediğini kontrol et
 */
export function isTurnstileLoaded(): boolean {
  return typeof window !== 'undefined' && typeof window.turnstile !== 'undefined';
}

/**
 * Cloudflare Turnstile script'ini yükle
 * @param scriptUrl - Script URL (opsiyonel, varsayılan: Cloudflare Turnstile)
 */
export function loadTurnstileScript(scriptUrl?: string): Promise<void> {
  if (isTurnstileLoaded()) {
    return Promise.resolve();
  }

  const url = scriptUrl || 'https://challenges.cloudflare.com/turnstile/v0/api.js';

  // Script zaten yükleniyorsa bekle
  if (document.querySelector(`script[src*="${url}"]`) || document.querySelector('script[src*="turnstile"]')) {
    return waitForTurnstile();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      waitForTurnstile().then(resolve).catch(reject);
    };
    script.onerror = () => {
      reject(new Error('Cloudflare Turnstile script yüklenemedi'));
    };
    document.head.appendChild(script);
  });
}

/**
 * Cloudflare Turnstile widget'ı render et
 * @param containerId - Widget'ın render edileceği container ID
 * @param siteKey - Turnstile site key (zorunlu)
 * @param callback - Token alındığında çağrılacak callback
 * @returns Widget ID (string)
 */
export function renderTurnstileWidget(
  containerId: string,
  siteKey: string,
  callback: (token: string) => void
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!siteKey) {
      reject(new Error('Turnstile site key gerekli'));
      return;
    }

    if (!isTurnstileLoaded()) {
      reject(new Error('Turnstile script yüklenmedi'));
      return;
    }

    try {
      const widgetId = window.turnstile!.render(containerId, {
        sitekey: siteKey,
        callback: (token: string) => {
          callback(token);
        },
        'expired-callback': () => {
          // Token süresi doldu, reset et
          window.turnstile!.reset(widgetId);
        },
        'error-callback': () => {
          reject(new Error('Turnstile hatası oluştu'));
        },
      });
      resolve(widgetId);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Turnstile widget'ını reset et
 * @param widgetId - Widget ID (opsiyonel)
 */
export function resetTurnstileWidget(widgetId?: string): void {
  if (isTurnstileLoaded() && window.turnstile) {
    window.turnstile.reset(widgetId);
  }
}

/**
 * Turnstile widget'ını kaldır
 * @param widgetId - Widget ID
 */
export function removeTurnstileWidget(widgetId: string): void {
  if (isTurnstileLoaded() && window.turnstile) {
    window.turnstile.remove(widgetId);
  }
}

/**
 * Turnstile token'ı al
 * @param widgetId - Widget ID
 * @returns Token string veya boş string
 */
export function getTurnstileToken(widgetId?: string): string {
  if (!isTurnstileLoaded() || !window.turnstile) {
    return '';
  }
  return window.turnstile.getResponse(widgetId) || '';
}

/**
 * Turnstile'ın hazır olmasını bekle
 */
export function waitForTurnstile(): Promise<void> {
  if (isTurnstileLoaded()) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const maxAttempts = 50; // 5 saniye (50 * 100ms)
    let attempts = 0;

    const checkInterval = setInterval(() => {
      attempts++;
      if (isTurnstileLoaded()) {
        clearInterval(checkInterval);
        resolve();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        reject(new Error('Turnstile script yüklenemedi'));
      }
    }, 100);
  });
}
